import React from 'react';
import { Container, Typography, Box, Grid, Card, CardContent } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Button } from '@mui/material';
import CodeRoundedIcon from '@mui/icons-material/CodeRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserRounded';
import EmojiObjectsRoundedIcon from '@mui/icons-material/EmojiObjectsRounded';

const Solutions: React.FC = () => {
  const navigate = useNavigate();
  const solutionCards = [
    {
      title: 'For Developers',
      description:
        'Test your APIs during development with our comprehensive testing tools. Integrate with your CI/CD pipeline and automate your testing workflow.',
      icon: CodeRoundedIcon,
      iconBg: 'linear-gradient(135deg, rgba(96,165,250,0.20), rgba(255,255,255,0.96))',
      iconColor: '#1d4ed8',
      accent: 'radial-gradient(circle at 0% 0%, rgba(96,165,250,0.22), transparent 55%)',
    },
    {
      title: 'For QA Teams',
      description:
        'Create comprehensive test suites with data sets and assertions. Track test results and generate reports for your stakeholders.',
      icon: FactCheckRoundedIcon,
      iconBg: 'linear-gradient(135deg, rgba(74,222,128,0.20), rgba(255,255,255,0.96))',
      iconColor: '#16a34a',
      accent: 'radial-gradient(circle at 0% 0%, rgba(74,222,128,0.24), transparent 55%)',
    },
    {
      title: 'For Enterprises',
      description:
        'Enterprise-grade security with schema-per-tenant isolation. Scale with confidence and maintain compliance with our Enterprise plan.',
      icon: VerifiedUserRoundedIcon,
      iconBg: 'linear-gradient(135deg, rgba(167,139,250,0.24), rgba(255,255,255,0.96))',
      iconColor: '#7c3aed',
      accent: 'radial-gradient(circle at 0% 0%, rgba(196,181,253,0.34), transparent 55%)',
    },
    {
      title: 'For Startups',
      description:
        'Start for free and scale as you grow. Our flexible plans adapt to your needs without breaking the bank.',
      icon: EmojiObjectsRoundedIcon,
      iconBg: 'linear-gradient(135deg, rgba(251,191,36,0.24), rgba(255,255,255,0.96))',
      iconColor: '#d97706',
      accent: 'radial-gradient(circle at 0% 0%, rgba(252,211,77,0.34), transparent 55%)',
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Box sx={{ py: 8 }}>
        <Typography
          variant="h2"
          component="h1"
          gutterBottom
          align="center"
          sx={{
            fontWeight: 700,
            color: '#0b2568',
            fontSize: { xs: '2.25rem', md: '3.35rem' },
            mb: 2,
          }}
        >
          Solutions for Every Team
        </Typography>
        <Typography
          variant="h6"
          color="text.secondary"
          align="center"
          paragraph
          sx={{ fontSize: { xs: '1.02rem', md: '1.12rem' }, mb: 5 }}
        >
          Find the perfect solution for your organization
        </Typography>

        <Grid container spacing={3.2} sx={{ mt: 1 }}>
          {solutionCards.map((card) => {
            const Icon = card.icon;
            return (
              <Grid key={card.title} item xs={12} sm={6} md={3}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    borderRadius: 2,
                    border: '1px solid #e6ebf5',
                    bgcolor: '#ffffff',
                    backgroundImage: card.accent,
                    backgroundRepeat: 'no-repeat',
                    transition: 'transform 0.28s ease, box-shadow 0.28s ease, border-color 0.28s ease',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      borderColor: '#c8d5ef',
                      boxShadow: '0 12px 22px rgba(15,23,42,0.11)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 3, textAlign: 'left' }}>
                    <Box
                      sx={{
                        width: 96,
                        height: 96,
                        borderRadius: '50%',
                        background: card.iconBg,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2.5,
                      }}
                    >
                      <Icon sx={{ fontSize: 50, color: card.iconColor }} />
                    </Box>
                    <Typography
                      variant="h5"
                      gutterBottom
                      sx={{
                        color: '#0b2568',
                        fontWeight: 700,
                        fontSize: { xs: '1.6rem', md: '2rem' },
                        mb: 1.2,
                      }}
                    >
                      {card.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: '#334155',
                        fontSize: '1rem',
                        lineHeight: 1.55,
                        mb: 0,
                      }}
                    >
                      {card.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/pricing')}
            sx={{
              px: 5,
              py: 1.1,
              borderRadius: 2,
              fontSize: '1.8rem',
              textTransform: 'none',
              fontWeight: 700,
              background: 'linear-gradient(180deg, #163f9f 0%, #0f2f7d 100%)',
              boxShadow: '0 8px 16px rgba(15,47,125,0.33)',
              '&:hover': {
                background: 'linear-gradient(180deg, #1a4dbf 0%, #13378f 100%)',
              },
            }}
          >
            View Pricing
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default Solutions;

